import { ORG_ID_HEADER } from "../auth/headers";
import { generatePresignedUrl, uploadFileToPresignedUrl } from "../services/upload.service";
import {
  CreatePreSignedUrlRequest,
  FileExtension,
  FileType,
} from "../types/upload.type";
import { UpdateOrganizationRequest } from "../types/organization.type";

export type LogoFileExtension = "png" | "jpg" | "jpeg";

export const ALLOWED_LOGO_EXTENSIONS: LogoFileExtension[] = ["png", "jpg", "jpeg"];
export const ALLOWED_LOGO_MIME_TYPES = ["image/png", "image/jpeg"] as const;
export const MAX_LOGO_BYTES = 2 * 1024 * 1024;

export type LogoChange = "unchanged" | "removed" | { file: File };

export function logoExtensionFromFilename(filename: string): LogoFileExtension | null {
  const ext = filename.split(".").pop()?.toLowerCase();
  if (ext === "png" || ext === "jpg" || ext === "jpeg") return ext;
  return null;
}

export function validateLogoFile(file: File): string | null {
  const ext = logoExtensionFromFilename(file.name);
  if (!ext) return "Logo must be PNG or JPEG";
  if (
    !ALLOWED_LOGO_MIME_TYPES.includes(
      file.type as (typeof ALLOWED_LOGO_MIME_TYPES)[number]
    )
  ) {
    if (file.type) return "Invalid image type";
  }
  if (file.size > MAX_LOGO_BYTES) return "Logo must be 2 MB or smaller";
  return null;
}

function logoFileExtension(file: File): FileExtension {
  const ext = logoExtensionFromFilename(file.name);
  if (!ext) throw new Error("Logo must be PNG or JPEG");
  return ext as FileExtension;
}

export async function uploadOrganizationLogo(
  file: File,
  options?: { organizationId?: string }
): Promise<{ assetId: string; filename: string }> {
  const validationError = validateLogoFile(file);
  if (validationError) throw new Error(validationError);

  const extension = logoFileExtension(file);
  const presignedReq: CreatePreSignedUrlRequest = {
    original_name: file.name,
  };
  const headers = options?.organizationId
    ? { [ORG_ID_HEADER]: options.organizationId }
    : undefined;

  const presigned = await generatePresignedUrl(
    presignedReq,
    FileType.ORGANIZATION_LOGO,
    extension,
    headers
  );

  const contentType =
    file.type || `image/${extension === "jpg" ? "jpeg" : extension}`;
  await uploadFileToPresignedUrl(file, presigned.upload_url, contentType);

  return { assetId: presigned.id, filename: file.name };
}

export async function resolveLogoFieldsForSubmit(
  logoChange: LogoChange,
  organizationId?: string
): Promise<Pick<UpdateOrganizationRequest, "logo_asset_id" | "logo_filename">> {
  if (logoChange === "unchanged") return {};
  if (logoChange === "removed") return { logo_asset_id: null };
  const uploaded = await uploadOrganizationLogo(logoChange.file, {
    organizationId,
  });
  return {
    logo_asset_id: uploaded.assetId,
    logo_filename: uploaded.filename,
  };
}

export function mergeLogoFieldsIntoPayload<T extends UpdateOrganizationRequest>(
  payload: T,
  logoFields: Pick<UpdateOrganizationRequest, "logo_asset_id" | "logo_filename">
): T {
  if (!("logo_asset_id" in logoFields)) return payload;
  const next = { ...payload, logo_asset_id: logoFields.logo_asset_id };
  if (logoFields.logo_asset_id != null) {
    next.logo_filename = logoFields.logo_filename ?? null;
  }
  return next;
}
